import { FullScreenAlertVariant, UseNotifications, useNotifications } from '@elearning-platform/ui';
import { useQueries, useQuery, UseQueryResult } from '@tanstack/react-query';
import { TransProps, useTranslation } from 'react-i18next';

import { QueryKey, RouteNameEnum } from '../../../enums';
import { useRedirectTo } from '../../../hooks';
import { UseRedirectTo } from '../../../interfaces';
import { EventDetails, Trainer, TrainerMetric } from '../trainingV2.types';

import { getEventDetails, getTrainerMetric } from './api';
import { UseEventDetailsProps, UseTrainerMetricsProps } from './hooks.types';

export const useEventDetails: ((params: UseEventDetailsProps) => UseQueryResult<EventDetails>)
  = (params: UseEventDetailsProps): UseQueryResult<EventDetails> => {
    const { showFullscreenAlert, hideFullscreenAlert }: UseNotifications = useNotifications();
    const { redirect }: UseRedirectTo = useRedirectTo();
    const { t }: TransProps<never> = useTranslation();

    return useQuery({
      queryKey: [QueryKey.Event, params.id],
      queryFn: (): Promise<EventDetails> => new Promise((resolve: (data: EventDetails) => void): void => {
        getEventDetails(Number(params.id))
          .then(resolve)
          // TODO: fix me
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .catch((e: any): void => {
            showFullscreenAlert({
              title: e?.response?.data?.message,
              variant: FullScreenAlertVariant.Dark,
              acceptButtonData: {
                label: t('competence.app.global.close'),
                onClick: (): void => {
                  redirect({ route: RouteNameEnum.Trainings });
                  hideFullscreenAlert();
                },
              },
            });
          });
      }),
      enabled: !!params.id,
    });
  };

export const useTrainerMetrics: ((params: UseTrainerMetricsProps) => UseQueryResult<TrainerMetric, Error>[])
  = (params: UseTrainerMetricsProps): UseQueryResult<TrainerMetric, Error>[] => useQueries({
    queries: params.eventData
      ? params.eventData.trainers.map((trainer: Trainer): {
        queryKey: (number | QueryKey)[];
        queryFn: () => Promise<TrainerMetric>;
        enabled: boolean;
      } => ({
        queryKey: [QueryKey.TrainerMetric, trainer.id],
        queryFn: (): Promise<TrainerMetric> => getTrainerMetric(trainer.id),
        enabled: !!trainer.id && !!trainer.hasMetric,
      }))
      : [],
  });
